import React, { useEffect, useRef } from 'react';
import { AlertTriangle, CheckCircle, Loader2, Trash2 } from 'lucide-react';
import type { InspectionItem } from '../../types';
import type { LoeschEntscheid } from '../../services/maengelLoeschen';

interface Props {
    item: InspectionItem;
    /** null = Archiv wird noch geprüft. */
    entscheid: LoeschEntscheid | null;
    language?: string;
    onErledigt: () => void;
    onVerwerfen: () => void;
    onAbbrechen: () => void;
}

const datumDe = (iso: string | null): string => {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? ` vom ${m[3]}.${m[2]}.${m[1]}` : '';
};

/**
 * Statt Löschen: ein Mangel, der im versendeten Bericht offen steht, wird
 * erledigt und nicht entfernt (Betreiber 06.10.2026, siehe maengelLoeschen.ts).
 */
export const MangelLoeschenDialog: React.FC<Props> = ({ item, entscheid, language, onErledigt, onVerwerfen, onAbbrechen }) => {
    const en = language === 'en';
    const hauptRef = useRef<HTMLButtonElement | null>(null);
    useEffect(() => {
        const el = hauptRef.current;
        if (el && typeof el.focus === 'function') el.focus();
    }, [entscheid]);
    useEffect(() => {
        const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onAbbrechen(); } };
        document.addEventListener('keydown', taste, true);
        return () => document.removeEventListener('keydown', taste, true);
    }, [onAbbrechen]);

    const name = `${item.ticketNumber ? `#${item.ticketNumber} ` : ''}${item.title || (en ? 'Untitled entry' : 'Eintrag ohne Titel')}`;
    const bericht = entscheid && 'nummer' in entscheid
        ? (en ? `report #${entscheid.nummer}` : `Bericht #${entscheid.nummer}${datumDe(entscheid.datum)}`)
        : '';

    let titel: string;
    let text: string;
    let warn = false;
    if (!entscheid) {
        titel = en ? 'Checking the archive …' : 'Archiv wird geprüft …';
        text = en ? 'Is this entry already in a sent report?' : 'Steht dieser Eintrag schon in einem versendeten Bericht?';
    } else if (entscheid.art === 'versendet_offen') {
        warn = true;
        titel = en ? 'Mark as resolved instead of deleting' : 'Statt löschen auf „erledigt“ setzen';
        text = en
            ? `“${name}” is listed as open in the sent PDF of ${bericht}. The client keeps it as open; deleting it would just make it vanish from the next report. Mark it as resolved instead.`
            : `„${name}“ ist im PDF von ${bericht} als offen erfasst und beim Kunden so angekommen. Gelöscht würde er im nächsten Bericht kommentarlos fehlen. Setze ihn statt dessen auf „erledigt“.`;
    } else if (entscheid.art === 'versendet_umgestellt') {
        warn = true;
        titel = en ? 'Not yet: wait for the next report' : 'Erst nach dem nächsten Bericht';
        text = en
            ? `“${name}” is listed as open in the sent PDF of ${bericht}. Its new status appears in the next report; after that it can be removed.`
            : `„${name}“ ist im PDF von ${bericht} als offen erfasst. Sein neuer Status erscheint im nächsten Bericht, danach kann er entfernt werden.`;
    } else if (entscheid.art === 'unbekannt') {
        warn = true;
        titel = en ? 'Could not check the archive' : 'Archiv nicht erreichbar';
        text = en
            ? `Without a connection it is unclear whether “${name}” is already in a sent report. If so, mark it as resolved instead of discarding it.`
            : `Ohne Verbindung lässt sich nicht prüfen, ob „${name}“ schon in einem versendeten Bericht steht. Wenn ja: lieber auf „erledigt“ setzen statt verwerfen.`;
    } else {
        titel = en ? 'Discard entry?' : 'Eintrag verwerfen?';
        text = en
            ? `“${name}” is not in any sent report yet and can be discarded.`
            : `„${name}“ steht noch in keinem versendeten Bericht und kann verworfen werden.`;
    }

    const kannErledigen = !!entscheid && (entscheid.art === 'versendet_offen' || (entscheid.art === 'unbekannt' && item.status === 'open'));
    const kannVerwerfen = !!entscheid && (entscheid.art === 'verwerfen' || entscheid.art === 'unbekannt');

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onAbbrechen}>
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="mangel-loeschen-titel"
                aria-describedby="mangel-loeschen-text"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-full overflow-y-auto border border-slate-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 flex gap-3 items-start">
                    <div className={`p-2 rounded-xl shrink-0 ${warn ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {!entscheid ? <Loader2 className="w-6 h-6 animate-spin" /> : warn ? <AlertTriangle className="w-6 h-6" /> : <Trash2 className="w-6 h-6" />}
                    </div>
                    <div className="min-w-0">
                        <h3 id="mangel-loeschen-titel" className="font-bold text-lg text-slate-800">{titel}</h3>
                        <p id="mangel-loeschen-text" className="text-sm text-slate-600 leading-relaxed mt-1">{text}</p>
                    </div>
                </div>
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col-reverse sm:flex-row justify-end gap-2">
                    <button
                        type="button"
                        onClick={onAbbrechen}
                        className="min-h-[44px] px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-200"
                    >
                        {en ? 'Cancel' : 'Abbrechen'}
                    </button>
                    {kannVerwerfen && (
                        <button
                            type="button"
                            ref={kannErledigen ? undefined : hauptRef}
                            onClick={onVerwerfen}
                            className={`min-h-[44px] px-4 py-2 rounded-xl text-sm font-bold ${kannErledigen ? 'text-red-700 hover:bg-red-50' : 'text-white bg-red-600 hover:bg-red-700'}`}
                        >
                            {entscheid?.art === 'unbekannt' ? (en ? 'Discard anyway' : 'Trotzdem verwerfen') : (en ? 'Discard' : 'Verwerfen')}
                        </button>
                    )}
                    {kannErledigen && (
                        <button
                            type="button"
                            ref={hauptRef}
                            onClick={onErledigt}
                            className="min-h-[44px] px-5 py-2 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center justify-center gap-2"
                        >
                            <CheckCircle className="w-4 h-4" /> {en ? 'Mark as resolved' : 'Auf erledigt setzen'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
